import { motion } from "framer-motion";
import { Award, Briefcase } from "lucide-react";

const About = () => {
  const advisoryBoard = [
    {
      name: "Sh. Anoop Kumar",
      title: "Founder Trustee and Director",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/i944czw8_anup.jpeg",
      bio: "Seasoned development professional with over 22 years of experience in organizational development, partnership building, local planning and governance. Founder and Director of PAHAD Trust, State Coordinator for RRAN-HP.",
      achievements: ["22+ years experience", "Master's in Urban Planning (SPA Delhi)", "State Coordinator RRAN-HP"],
    },
    {
      name: "Sh. Malkit Singh",
      title: "Trustee",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/9h01in85_MALKIT%20SINGH.jpeg",
      bio: "A dedicated trustee of PAHAD Trust, bringing valuable expertise and commitment to the organization's mission of empowering Himalayan communities through sustainable development and traditional farming practices.",
      achievements: ["Trustee of PAHAD Trust", "Community Development Advocate", "Supporter of Sustainable Agriculture"],
    },
    {
      name: "Shri Nek Ram Sharma",
      title: "Sr. Advisor (Padma Shri Awardee)",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/xulpmqhm_nk.jpeg",
      bio: "Brings decades of experience in grassroots development and sustainable agriculture. Provides strategic guidance to PAHAD Trust with strong focus on building capacities of farmers and strengthening community-based institutions.",
      achievements: ["Padma Shri Awardee", "Decades in grassroots development", "Expert in sustainable agriculture"],
    },
    {
      name: "Dr. S.C. Sharma",
      title: "Sr. Technical Advisor",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/tsivdh8r_Dr%20sc%20sharmaa.jpeg",
      bio: "Former Director Regional Research Station Kandi Area & KVKs, Punjab Agricultural University Ludhiana. Expert in watershed management, soil and water conservation, and natural farming.",
      achievements: ["37 years research experience", "Expert in watershed management", "Soil & water conservation specialist"],
    },
  ];

  const partners = [
    "M3M Foundation",
    "SPJIMR DOCC",
    "H2O Not on Map",
    "HimERA Network",
    "Holistic Himalayas",
    "Himalaya Bachao Samiti",
    "NABARD",
    "Krishi Vigyan Kendra",
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1701153758624-4b3d352bcfbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwaGltYWxheWFzJTIwbW91bnRhaW4lMjBsYW5kc2NhcGUlMjBkaGFyYW1zaGFsYXxlbnwwfHx8fDE3NzQ3NjM2OTd8MA&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/50" />
        </div>
        <div className="relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
          >
            About PAHAD Trust
          </motion.h1>
        </div>
      </section>

      {/* History */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#1C221F] mb-6">
              Our Story
            </h2>
            <div className="space-y-6 text-base leading-relaxed text-[#515E58] text-left">
              <p>
                PAHAD Trust (Professional Assistance for Hill Area Development) is a grassroots, public charitable organization established in 2018 in Himachal Pradesh with a vision to strengthen sustainable and inclusive development in the Himalayan region.
              </p>
              <p>
                The Trust is committed to the upliftment of marginalized and vulnerable communities, with a strong focus on women, small and marginal farmers, youth, and rural households. It works at the intersection of climate-resilient agriculture, sustainable livelihoods, traditional knowledge systems, and participatory local governance, enabling communities to lead their own development processes.
              </p>
              <p>
                Over the past few years, the Trust has directly engaged with nearly 2000 farmers across 50 small villages in Chamba district, one of the most remote and aspirational regions of India. Through sustained community engagement, it has contributed to increased adoption of sustainable farming practices, strengthened women-led groups, and enhanced local awareness on biodiversity conservation and food systems.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advisory Board */}
      <section className="py-24 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#1C221F] mb-4">
              Advisory Board
            </h2>
            <p className="text-base text-[#515E58] max-w-2xl mx-auto">
              Guided by leaders with decades of experience in sustainable development
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {advisoryBoard.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`advisory-member-${index + 1}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#E4E2DC]/40 card-hover"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-medium text-[#1C221F] mb-1">
                    {member.name}
                  </h3>
                  <p className="text-sm text-[#8C9A74] mb-4 flex items-center">
                    {member.title.includes("Padma Shri") && (
                      <Award className="h-4 w-4 mr-1 text-[#C86A4C]" />
                    )}
                    {member.title}
                  </p>
                  <p className="text-sm text-[#515E58] mb-4 leading-relaxed">
                    {member.bio}
                  </p>
                  <div className="space-y-2">
                    {member.achievements.map((achievement, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <Briefcase className="h-4 w-4 text-[#2B5C45] flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-[#515E58]">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16 bg-[#8C9A74]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-heading font-medium text-center text-[#1C221F] mb-12"
          >
            Our Partners & Collaborators
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/c5tjfr84_WhatsApp%20Image%202026-03-29%20at%2009.24.18.jpeg"
                alt="M3M Foundation"
                className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/6ny1zhh0_WhatsApp%20Image%202026-03-29%20at%2009.24.23.jpeg"
                alt="SPJIMR DOCC"
                className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/h31n51w4_WhatsApp%20Image%202026-03-29%20at%2009.24.19.jpeg"
                alt="NotOnMap"
                className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex justify-center"
            >
              <div className="text-center">
                <p className="text-lg font-medium text-[#2B5C45]">NABARD</p>
                <p className="text-sm text-[#515E58]">& More</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
